/**
 * Room-edge wall picker — no-skeleton fallback for the Door/Window tool.
 *
 * When a floor has no `PointoramaWallSketch`, walls can still be selected via
 * the room polygons that bound them. Adjacent rooms are interior polygons
 * with a gap = wall thickness between them (see `FloorProcessors` skeleton
 * expansion — rooms are inset from the wall centerline by `innerWallThickness/2`).
 * That gap is what the perpendicular-ray test below traverses to identify a
 * "wall between two rooms" from a single hovered edge.
 *
 * Algorithm:
 *   1. Project every room polygon edge to pixel space; pick the closest one
 *      to the cursor within `HOVER_PX_RADIUS`. That's edge A.
 *   2. Compute the foot of the cursor's perpendicular on edge A (world space).
 *   3. Cast a perpendicular ray in 2D from that foot; find the **first**
 *      intersection across every other room-polygon segment.
 *   4. If the first hit belongs to the same room as A → abort (the cursor is
 *      pointed at an interior partition that the algorithm can't resolve into
 *      a between-rooms wall).
 *   5. Otherwise, build a wall box: take the longer of the two edges as the
 *      box's long axis. The box runs the longer edge's full length and spans
 *      the perpendicular gap between the two edges (perpendicular extent =
 *      min ‖foot - hit‖, max ‖foot - hit‖ from the projection of the shorter
 *      edge's endpoints onto the longer edge's perpendicular direction).
 *
 * Outputs a `RoomEdgePick` that's a drop-in for the skeleton edge pick
 * downstream: the tool uses it to render hover and to synthesise a
 * `WallFocusSpec` for the click-to-enter-segmentation flow.
 *
 * Rendering: `RoomEdgeHoverOverlay` mounts a translucent quad on the scene's
 * THREE.Scene and updates it from a `RoomEdgePick` (or hides on null). The
 * overlay survives across hover frames so we don't churn GPU buffers; only
 * positions get rewritten.
 */

import * as THREE from 'three';
import { PointoramaRoom } from '../../../utils/PointoramaRoom';
import { PointoramaOutline } from '../../../utils/PointoramaOutline';
import { PixelHitTester } from './selection/PixelHitTester';
import { Viewer } from '../../../viewer/viewer';
import type { WallFocusSpec } from './WallFocus';
import type { PointoramaWallSketch, SkeletonNodeData, SkeletonEdgeData } from '../../../utils/PointoramaWallSketch';

/**
 * One edge of a room polygon, exposed as a pure value the picker can iterate
 * without holding a Room reference. `roomIdentifier` lets the perpendicular-
 * ray test filter "hits the same room as the picked edge" without re-deriving
 * containment from polygon math.
 */
export interface RoomEdgeSegment {
  roomIdentifier: string;
  /** World-space start of the polygon edge. */
  a: THREE.Vector3;
  /** World-space end of the polygon edge. */
  b: THREE.Vector3;
}

/**
 * Result of a successful room-edge pick. The cursor is on `pickedEdge`,
 * `wallBox` is the world-space quad covering the wall (4 corners, CCW from
 * above, on the floor Z so the overlay renders flat on the floor plane).
 *
 * `oppositeEdge` is the edge from the *other* room when a real between-rooms
 * wall is found within `MAX_PERP_SEARCH_DISTANCE_M`, or `null` when the
 * picker fell back to the exterior-wall path (no opposite-room edge in reach
 * or the only hit was on the same room).
 */
export interface RoomEdgePick {
  pickedEdge: RoomEdgeSegment;
  oppositeEdge: RoomEdgeSegment | null;
  /** 4 corners of the wall box, world-space, all at `floorZ`. */
  wallBox: [THREE.Vector3, THREE.Vector3, THREE.Vector3, THREE.Vector3];
  /** Direction of the long axis (longer of the two edges), unit vector in XY. */
  longAxis: { x: number; y: number };
  /** Centerline endpoints along the long axis — feed straight into `WallFocusSpec`. */
  centerlineStart: THREE.Vector3;
  centerlineEnd: THREE.Vector3;
  /** Wall thickness (perpendicular extent of the box), meters. */
  wallThickness: number;
}

const EPS = 1e-6;

/**
 * Cap on the perpendicular-ray search distance for the "opposite wall" edge.
 * Beyond this the ray almost certainly punched out of the building through
 * an open courtyard / exterior gap and would highlight a wall on a different
 * structure entirely — better to treat the picked wall as an exterior wall
 * and use a fixed-thickness fallback box.
 */
const MAX_PERP_SEARCH_DISTANCE_M = 2;

/**
 * Wall thickness assumed when no opposite room edge is found within
 * `MAX_PERP_SEARCH_DISTANCE_M` (typical exterior wall: insulation + brick).
 * The box on the cursor's side of the picked edge spans this much into
 * "outside the building".
 */
const EXTERIOR_WALL_THICKNESS_M = 0.3;

/**
 * Flatten the rooms on the active floor into their polygon edges. Skips
 * polygons with fewer than 3 vertices (degenerate). Closes the polygon by
 * connecting the last vertex back to the first.
 */
export function collectRoomEdges(rooms: PointoramaRoom[]): RoomEdgeSegment[] {
  const out: RoomEdgeSegment[] = [];
  for (const room of rooms) {
    const poly = room.getPolygon();
    if (poly.length < 3) continue;
    for (let i = 0; i < poly.length; i++) {
      const a = poly[i];
      const b = poly[(i + 1) % poly.length];
      out.push({ roomIdentifier: room.identifier, a: a.clone(), b: b.clone() });
    }
  }
  return out;
}

/**
 * Flatten outlines into wall-edge segments, so the Door/Window tool treats an
 * outline boundary (outer ring + cavity rings) exactly like room walls. The
 * outline's identifier tags each edge in place of a room id — this is opaque
 * to the picker (it only uses it for same-source rejection), so outline edges
 * compose seamlessly with room edges. Outlines have no opposite-room edge
 * across the gap, so they resolve via the exterior-wall fallback box, which is
 * the correct read for a building perimeter.
 */
export function collectOutlineEdges(outlines: PointoramaOutline[]): RoomEdgeSegment[] {
  const out: RoomEdgeSegment[] = [];
  for (const outline of outlines) {
    for (const edge of outline.getAllEdges()) {
      out.push({ roomIdentifier: outline.identifier, a: edge.start.clone(), b: edge.end.clone() });
    }
  }
  return out;
}

/**
 * Pick the closest room edge to `mousePx`. No proximity cap — the cursor
 * just needs to be on-screen relative to at least one room edge. Inside
 * rooms / outside the building / anywhere on the floor view, the nearest
 * room edge wins. The "no max range" UX choice is deliberate: the fallback
 * is meant to read as "point at a wall, anywhere on the plan, and it
 * highlights" — bounding it by a pixel radius would re-introduce the
 * close-proximity feel the user explicitly wanted gone.
 *
 * Returns null only when every edge fails to project (all rooms off-screen),
 * which in practice means the camera is somewhere unreasonable.
 */
function pickClosestRoomEdge(
  edges: RoomEdgeSegment[],
  mousePx: THREE.Vector2,
  hitTester: PixelHitTester,
): RoomEdgeSegment | null {
  let best: RoomEdgeSegment | null = null;
  let bestDist = Infinity;
  for (const e of edges) {
    const ap = hitTester.projectWorldToPixel(e.a);
    const bp = hitTester.projectWorldToPixel(e.b);
    if (!ap || !bp) continue;
    const d = PixelHitTester.pointToSegmentDistancePx(mousePx, ap, bp);
    if (d < bestDist) {
      bestDist = d;
      best = e;
    }
  }
  return best;
}

/**
 * Intersect a ray (origin + direction, both XY) with a segment. Returns the
 * parametric distance along the ray to the intersection, or null if there
 * isn't one in the forward direction.
 *
 * Uses the standard 2D segment-segment formula: solve
 *   origin + t·rayDir = a + u·(b - a),  t ≥ 0, u ∈ [0, 1].
 */
function raySegmentIntersect2D(
  originX: number,
  originY: number,
  rayDirX: number,
  rayDirY: number,
  ax: number,
  ay: number,
  bx: number,
  by: number,
): number | null {
  const sx = bx - ax;
  const sy = by - ay;
  // det = ray × seg. Zero means parallel — no unique intersection. (Co-linear
  // overlap is also rejected here; the caller wouldn't get a usable wall from
  // an edge co-linear with the ray anyway.)
  const det = rayDirX * sy - rayDirY * sx;
  if (Math.abs(det) < EPS) return null;
  const dx = ax - originX;
  const dy = ay - originY;
  const t = (dx * sy - dy * sx) / det;
  const u = (dx * rayDirY - dy * rayDirX) / det;
  if (t < 0) return null;
  if (u < 0 || u > 1) return null;
  return t;
}

/**
 * Cast a perpendicular ray from `cursorWorld` against every edge in `edges`,
 * return the **first** hit (smallest t > 0). Ray direction is fixed by the
 * caller — the cursor is outside every room polygon, so the cursor's side of
 * `pickedEdge` is known and we cast in exactly one direction (toward the
 * picked edge and through it). `pickedEdge` itself is excluded from the test
 * by reference identity; the first hit on any *other* edge is what the
 * caller's "same-room → abort" rule operates on.
 */
function firstPerpendicularHit(
  edges: RoomEdgeSegment[],
  pickedEdge: RoomEdgeSegment,
  originX: number,
  originY: number,
  dirX: number,
  dirY: number,
): { edge: RoomEdgeSegment; distance: number } | null {
  let bestEdge: RoomEdgeSegment | null = null;
  let bestT = MAX_PERP_SEARCH_DISTANCE_M;
  for (const e of edges) {
    if (e === pickedEdge) continue;
    const t = raySegmentIntersect2D(originX, originY, dirX, dirY, e.a.x, e.a.y, e.b.x, e.b.y);
    if (t === null) continue;
    // Reject zero-distance hits — those are co-incident edges (different
    // rooms sharing a vertex, or numerical noise at a junction) and would
    // collapse the wall box. A real wall has measurable thickness; sub-mm
    // distances are noise.
    if (t < 1e-3) continue;
    if (t < bestT) {
      bestT = t;
      bestEdge = e;
    }
  }
  if (!bestEdge) return null;
  return { edge: bestEdge, distance: bestT };
}

/**
 * Build the wall box for an exterior wall — no opposite room edge exists
 * within `MAX_PERP_SEARCH_DISTANCE_M`, so we extrude the picked edge by a
 * fixed `EXTERIOR_WALL_THICKNESS_M` in the cursor's direction. The result
 * is geometrically equivalent to what `buildWallBox` would produce given
 * a parallel opposite edge `EXTERIOR_WALL_THICKNESS_M` away — sharing the
 * same downstream shape lets the rest of the pipeline ignore the
 * exterior-vs-between-rooms distinction.
 *
 * `dirX/dirY` is the unit perpendicular pointing from the picked room's
 * interior toward "outside" (same direction the perpendicular ray was
 * cast in `pickRoomEdgeAtPixel`).
 */
function buildExteriorWallBox(
  pickedEdge: RoomEdgeSegment,
  dirX: number,
  dirY: number,
  floorZ: number,
): { wallBox: RoomEdgePick['wallBox']; longAxis: RoomEdgePick['longAxis']; centerlineStart: THREE.Vector3; centerlineEnd: THREE.Vector3; wallThickness: number } {
  const a = pickedEdge.a;
  const b = pickedEdge.b;
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const len = Math.hypot(dx, dy);
  if (len < EPS) {
    const p = new THREE.Vector3(a.x, a.y, floorZ);
    return {
      wallBox: [p.clone(), p.clone(), p.clone(), p.clone()],
      longAxis: { x: 1, y: 0 },
      centerlineStart: p.clone(),
      centerlineEnd: p.clone(),
      wallThickness: 0,
    };
  }
  const ux = dx / len;
  const uy = dy / len;
  const thickness = EXTERIOR_WALL_THICKNESS_M;

  // Box corners: picked edge at perp=0, exterior face at perp=thickness
  // along (dirX, dirY).
  const c0 = new THREE.Vector3(a.x, a.y, floorZ);
  const c1 = new THREE.Vector3(b.x, b.y, floorZ);
  const c2 = new THREE.Vector3(b.x + dirX * thickness, b.y + dirY * thickness, floorZ);
  const c3 = new THREE.Vector3(a.x + dirX * thickness, a.y + dirY * thickness, floorZ);

  const half = thickness / 2;
  const centerlineStart = new THREE.Vector3(a.x + dirX * half, a.y + dirY * half, floorZ);
  const centerlineEnd = new THREE.Vector3(b.x + dirX * half, b.y + dirY * half, floorZ);

  return {
    wallBox: [c0, c1, c2, c3],
    longAxis: { x: ux, y: uy },
    centerlineStart,
    centerlineEnd,
    wallThickness: thickness,
  };
}

/** Even-odd point-in-polygon test in XY. */
function pointInPolygon2D(x: number, y: number, polygon: THREE.Vector3[]): boolean {
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i].x;
    const yi = polygon[i].y;
    const xj = polygon[j].x;
    const yj = polygon[j].y;
    if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) {
      inside = !inside;
    }
  }
  return inside;
}

/**
 * Build the wall box from the two edges. The **longer** edge defines the
 * box's long axis (full length); the shorter edge contributes only its
 * perpendicular distance to the longer edge.
 *
 * Geometry:
 *   - Longer edge endpoints L0, L1 → long axis u = (L1 - L0) / |L1 - L0|.
 *   - Perpendicular v = rotate(u, 90°).
 *   - Project shorter edge endpoints S0, S1 onto v relative to L0 → signed
 *     perpendicular offsets pS0, pS1.
 *   - The longer edge sits at perp = 0. The wall slab spans perp ∈ [min(0,
 *     pS0, pS1), max(0, pS0, pS1)]. In practice the two shorter-edge
 *     projections cluster on the same side (the opposite room is on one
 *     side of the wall), so the slab is essentially [0, distance-to-other-room].
 *   - Box corners: L0 + perpLo·v, L1 + perpLo·v, L1 + perpHi·v, L0 + perpHi·v.
 *
 * All Z values are pinned to `floorZ` so the box renders flat on the floor
 * plane — the overlay is a 2D top-down indicator, not a 3D wall.
 */
function buildWallBox(
  pickedEdge: RoomEdgeSegment,
  oppositeEdge: RoomEdgeSegment,
  floorZ: number,
): { wallBox: RoomEdgePick['wallBox']; longAxis: RoomEdgePick['longAxis']; centerlineStart: THREE.Vector3; centerlineEnd: THREE.Vector3; wallThickness: number } {
  const pickedLen = Math.hypot(pickedEdge.b.x - pickedEdge.a.x, pickedEdge.b.y - pickedEdge.a.y);
  const oppLen = Math.hypot(oppositeEdge.b.x - oppositeEdge.a.x, oppositeEdge.b.y - oppositeEdge.a.y);
  const longer = pickedLen >= oppLen ? pickedEdge : oppositeEdge;
  const shorter = longer === pickedEdge ? oppositeEdge : pickedEdge;

  const L0 = longer.a;
  const L1 = longer.b;
  const dx = L1.x - L0.x;
  const dy = L1.y - L0.y;
  const len = Math.hypot(dx, dy);
  // Fallback can't be exercised meaningfully when the longer edge is
  // degenerate — return a flat box co-located with L0 and let the caller
  // bail. We only reach here from a perpendicular hit so this shouldn't fire
  // in practice.
  if (len < EPS) {
    const p = new THREE.Vector3(L0.x, L0.y, floorZ);
    return {
      wallBox: [p.clone(), p.clone(), p.clone(), p.clone()],
      longAxis: { x: 1, y: 0 },
      centerlineStart: p.clone(),
      centerlineEnd: p.clone(),
      wallThickness: 0,
    };
  }
  const ux = dx / len;
  const uy = dy / len;
  // +90° rotation of u in XY.
  const vx = -uy;
  const vy = ux;

  // Signed perpendicular offsets of S0/S1 relative to L0, projected onto v.
  const projPerp = (p: THREE.Vector3): number => (p.x - L0.x) * vx + (p.y - L0.y) * vy;
  const pS0 = projPerp(shorter.a);
  const pS1 = projPerp(shorter.b);
  // Longer edge sits at perp = 0 by construction; include it as one of the
  // candidates so the box always contains the longer edge.
  const perpLo = Math.min(0, pS0, pS1);
  const perpHi = Math.max(0, pS0, pS1);
  const wallThickness = perpHi - perpLo;

  const corner = (alongStart: boolean, perp: number): THREE.Vector3 => {
    const base = alongStart ? L0 : L1;
    return new THREE.Vector3(base.x + perp * vx, base.y + perp * vy, floorZ);
  };
  const c0 = corner(true, perpLo);
  const c1 = corner(false, perpLo);
  const c2 = corner(false, perpHi);
  const c3 = corner(true, perpHi);

  const midPerp = (perpLo + perpHi) / 2;
  const centerlineStart = new THREE.Vector3(L0.x + midPerp * vx, L0.y + midPerp * vy, floorZ);
  const centerlineEnd = new THREE.Vector3(L1.x + midPerp * vx, L1.y + midPerp * vy, floorZ);

  return {
    wallBox: [c0, c1, c2, c3],
    longAxis: { x: ux, y: uy },
    centerlineStart,
    centerlineEnd,
    wallThickness,
  };
}

/**
 * Top-level entry point. Returns null when:
 *  - an outline exists on the floor and the cursor is OUTSIDE it (the outline
 *    is a hard outer boundary — nothing is selectable beyond it);
 *  - the cursor is inside any room polygon (you're inside a space, not
 *    pointing at a wall);
 *  - no edge is pickable (all rooms off-screen).
 *
 * Otherwise produces a `RoomEdgePick`. Three outcomes for the perpendicular
 * ray from the cursor:
 *   1. Hits an edge from a *different* room within `MAX_PERP_SEARCH_DISTANCE_M`
 *      → real between-rooms wall, box spans the gap between the two edges.
 *   2. Hits an edge from the *same* room (interior partition / re-entry into
 *      the picked room across an irregular polygon) OR no hit within
 *      `MAX_PERP_SEARCH_DISTANCE_M` → treat the picked edge as an exterior
 *      wall and synthesise a box `EXTERIOR_WALL_THICKNESS_M` thick on the
 *      cursor's side.
 *
 * Ray origin is the cursor itself, not the perpendicular foot on the picked
 * edge: the user's intent is "the wall on the side of my cursor", so we
 * cast from where the cursor is, perpendicular to the picked edge, in the
 * direction that travels *through* the picked edge. That direction is the
 * sign of (cursor - foot) along the edge's perpendicular — pointing from
 * the cursor toward the picked room's interior. The opposite room's edge
 * sits along that ray, past the picked edge.
 *
 * The returned `RoomEdgePick.centerlineStart/End` and `wallThickness` are
 * fed directly to `WallFocusSpec` upstream — the room-edge picker is the
 * sole producer of a fallback wall spec, so collapsing those derived values
 * into the return type avoids a second pass through the geometry.
 */
export function pickRoomEdgeAtPixel(
  edges: RoomEdgeSegment[],
  rooms: PointoramaRoom[],
  mousePx: THREE.Vector2,
  cursorWorld: THREE.Vector3,
  hitTester: PixelHitTester,
  floorZ: number,
  outlineRings: THREE.Vector3[][] = [],
  opts: {
    /**
     * Also pick when the cursor is INSIDE a room: the pick is restricted to
     * that room's own edges, the perpendicular ray casts OUTWARD through the
     * picked edge, and `maxCursorEdgeDistanceM` bounds the reach. Used by
     * placements that wall-align from within the room (e.g. a bath dropped
     * near a wall) — the default rejects room interiors to avoid a
     * magnet-to-walls hover UX.
     */
    allowInsideRoom?: boolean;
    /** World-meter cap on cursor→picked-edge distance (inside-room picks only). */
    maxCursorEdgeDistanceM?: number;
  } = {},
): RoomEdgePick | null {
  // Outline acts as a hard outer boundary: when one exists on the floor, the
  // cursor must be INSIDE it to pick anything. Outside the outline there is no
  // selection at all (not even the outline's own edge — so the outline edge is
  // pickable only from its interior side). With no outline present this gate is
  // empty and room walls stay pickable from any side (unchanged behavior).
  if (outlineRings.length > 0) {
    const insideAnyOutline = outlineRings.some(
      (ring) => ring.length >= 3 && pointInPolygon2D(cursorWorld.x, cursorWorld.y, ring),
    );
    if (!insideAnyOutline) return null;
  }

  // Inside-any-room → not a wall pick (default). Cursor in a room interior
  // means the user is pointing at empty floor space, not at a wall — the
  // fallback would otherwise still pick the nearest wall and highlight it,
  // producing a "magnet to walls" UX. `allowInsideRoom` opts into the
  // interior mode instead: the pick targets the CONTAINING room's own
  // boundary, cast outward.
  let containingRoom: PointoramaRoom | null = null;
  for (const room of rooms) {
    const poly = room.getPolygon();
    if (poly.length < 3) continue;
    if (pointInPolygon2D(cursorWorld.x, cursorWorld.y, poly)) {
      if (!opts.allowInsideRoom) return null;
      containingRoom = room;
      break;
    }
  }

  const candidateEdges = containingRoom ? edges.filter((e) => e.roomIdentifier === containingRoom.identifier) : edges;
  const pickedEdge = pickClosestRoomEdge(candidateEdges, mousePx, hitTester);
  if (!pickedEdge) return null;

  const ex = pickedEdge.b.x - pickedEdge.a.x;
  const ey = pickedEdge.b.y - pickedEdge.a.y;
  const elen = Math.hypot(ex, ey);
  if (elen < EPS) return null;

  // Inside-room reach cap: alignment engages near the wall, not from the
  // middle of a large room.
  if (containingRoom && opts.maxCursorEdgeDistanceM !== undefined) {
    const t = Math.max(0, Math.min(1, ((cursorWorld.x - pickedEdge.a.x) * ex + (cursorWorld.y - pickedEdge.a.y) * ey) / (elen * elen)));
    const footX = pickedEdge.a.x + t * ex;
    const footY = pickedEdge.a.y + t * ey;
    if (Math.hypot(cursorWorld.x - footX, cursorWorld.y - footY) > opts.maxCursorEdgeDistanceM) return null;
  }

  // Cast the ray from the cursor *away* from the picked edge, staying on the
  // cursor's side. Geometry: rooms are interior polygons inset from the wall
  // centerline (FloorProcessors skeleton expansion). When the cursor sits in
  // a wall gap, the picked edge belongs to whichever room is closer in pixel
  // space; that room's interior is on the *opposite* side from the cursor,
  // and the *other* room's edge is on the cursor's side, further out.
  //
  // So: signed perp distance of cursor → sign tells us which perpendicular
  // direction is "away from the picked room's interior". Going in that
  // direction from the cursor lands first on the opposite room's edge.
  //
  // INSIDE-ROOM mode inverts that: the cursor sits in the picked room's
  // interior, so the wall gap (and the opposite room's edge) lies on the
  // OTHER side — cast through the picked edge, outward.
  const perpX = -ey / elen;
  const perpY = ex / elen;
  const cursorSide = (cursorWorld.x - pickedEdge.a.x) * perpX + (cursorWorld.y - pickedEdge.a.y) * perpY;
  if (Math.abs(cursorSide) < EPS) return null; // cursor sits exactly on the edge — degenerate
  const outwardFlip = containingRoom ? -1 : 1;
  const sign = (cursorSide > 0 ? 1 : -1) * outwardFlip;
  const dirX = perpX * sign;
  const dirY = perpY * sign;

  const hit = firstPerpendicularHit(edges, pickedEdge, cursorWorld.x, cursorWorld.y, dirX, dirY);
  // No hit within `MAX_PERP_SEARCH_DISTANCE_M`, OR first hit is on the same
  // room (interior partition / odd polygon shape) → fall back to a
  // fixed-thickness exterior wall box. Either case means "no usable opposite
  // wall edge within reach"; the fallback gives the user something to click
  // instead of a no-op hover.
  if (!hit || hit.edge.roomIdentifier === pickedEdge.roomIdentifier) {
    const box = buildExteriorWallBox(pickedEdge, dirX, dirY, floorZ);
    return {
      pickedEdge,
      oppositeEdge: null,
      wallBox: box.wallBox,
      longAxis: box.longAxis,
      centerlineStart: box.centerlineStart,
      centerlineEnd: box.centerlineEnd,
      wallThickness: box.wallThickness,
    };
  }

  const box = buildWallBox(pickedEdge, hit.edge, floorZ);
  return {
    pickedEdge,
    oppositeEdge: hit.edge,
    wallBox: box.wallBox,
    longAxis: box.longAxis,
    centerlineStart: box.centerlineStart,
    centerlineEnd: box.centerlineEnd,
    wallThickness: box.wallThickness,
  };
}

/**
 * Convert a `RoomEdgePick` to a `WallFocusSpec`. The fallback path doesn't
 * have a skeleton's `leftSize`/`rightSize` split, so the wall thickness is
 * distributed evenly across both sides of the centerline. Z extents come
 * from the floor/ceiling planes the tool already resolves per-floor.
 */
export function buildWallFocusSpecFromRoomEdgePick(
  pick: RoomEdgePick,
  floorZ: number,
  ceilingZ: number,
  floorGroupId: string | null,
): WallFocusSpec {
  const halfThickness = Math.max(0.05, pick.wallThickness / 2);
  return {
    start: pick.centerlineStart.clone().setZ(floorZ),
    end: pick.centerlineEnd.clone().setZ(floorZ),
    leftSize: halfThickness,
    rightSize: halfThickness,
    floorZ,
    ceilingZ,
    floorGroupId,
  };
}

/**
 * Persist the sketch's CURRENT node/edge data through the same
 * `edit_skeleton` event the wall editor commits with. Used to promote a
 * transiently-inserted (imagined) wall edge to a real, saved one.
 */
export function persistWallSketch(viewer: Viewer, sketch: PointoramaWallSketch): void {
  viewer.scene.dispatchEvent({
    type: 'edit_skeleton',
    value: {
      annotationIdentifier: sketch.identifier,
      nodes: sketch.getNodes(),
      edges: sketch.getAllEdges(),
    },
  });
}

/**
 * Remove a loose wall edge previously inserted by
 * {@link materializeWallEdgeFromRoomPick} (its two endpoint nodes are removed
 * with it when nothing else references them). Data-only — no persistence
 * event; use for transient "imagined" edges that were never persisted.
 */
export function removeLooseWallEdge(sketch: PointoramaWallSketch, edgeId: string): void {
  const edge = sketch.getAllEdges().find((e) => e.id === edgeId);
  if (!edge) return;
  const edges = sketch.getAllEdges().filter((e) => e.id !== edgeId);
  const orphaned = new Set([edge.startNodeId, edge.endNodeId]);
  for (const e of edges) {
    orphaned.delete(e.startNodeId);
    orphaned.delete(e.endNodeId);
  }
  const nodes = sketch.getNodes().filter((n) => !orphaned.has(n.id));
  sketch.setData(nodes, edges, sketch.getMetadata());
}

/**
 * Insert a loose (disconnected) wall edge into `sketch` from a room/outline-
 * edge pick: two new nodes at the pick's centerline endpoints and one edge
 * whose left/right sizes split the measured gap thickness. By default it
 * persists through the same `edit_skeleton` event the wall editor commits
 * with; pass `persist: false` for a TRANSIENT (imagined) edge that a later
 * {@link persistWallSketch} promotes or {@link removeLooseWallEdge} discards.
 * The edge is deliberately NOT connected to the rest of the skeleton — the
 * user can connect it later in the wall editor.
 *
 * Shared by every tool that "imagines" a wall from the room/outline fallback
 * and materializes it on interaction (door/window, sketch objects).
 * Returns the new edge's id.
 */
export function materializeWallEdgeFromRoomPick(viewer: Viewer, sketch: PointoramaWallSketch, pick: RoomEdgePick, opts: { persist?: boolean } = {}): string {
  const half = Math.max(0.05, pick.wallThickness / 2);
  const z = sketch.getFloorHeight();
  const nodeAId = crypto.randomUUID();
  const nodeBId = crypto.randomUUID();
  const edgeId = crypto.randomUUID();
  const nameSuffix = `${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`;

  // Adjacent-room membership: only ids that are actual rooms on this floor
  // (outline picks carry the OUTLINE identifier, which must not leak into
  // roomIds).
  const roomIdSet = new Set(viewer.scene.rooms.filter((r) => r.groupIdentifier === sketch.groupIdentifier).map((r) => r.identifier));
  const roomIds = [pick.pickedEdge.roomIdentifier, pick.oppositeEdge?.roomIdentifier].filter((id): id is string => !!id && roomIdSet.has(id));
  const uniqueRoomIds = [...new Set(roomIds)];

  const nodeA: SkeletonNodeData = {
    id: nodeAId,
    name: `node_${nameSuffix}_a`,
    position: { x: pick.centerlineStart.x, y: pick.centerlineStart.y, z },
    roomIds: uniqueRoomIds,
    edgeIds: [edgeId],
    metadata: '',
  };
  const nodeB: SkeletonNodeData = {
    id: nodeBId,
    name: `node_${nameSuffix}_b`,
    position: { x: pick.centerlineEnd.x, y: pick.centerlineEnd.y, z },
    roomIds: uniqueRoomIds,
    edgeIds: [edgeId],
    metadata: '',
  };
  const edge: SkeletonEdgeData = {
    id: edgeId,
    name: `edge_${nameSuffix}`,
    startNodeId: nodeAId,
    endNodeId: nodeBId,
    roomIds: uniqueRoomIds,
    leftSize: half,
    rightSize: half,
    // Between two rooms = interior wall; no opposite edge in reach = the
    // building perimeter (exterior thickness fallback box).
    wallType: pick.oppositeEdge ? 'INNER' : 'OUTER',
  };

  const nodes = [...sketch.getNodes(), nodeA, nodeB];
  const edges = [...sketch.getAllEdges(), edge];
  sketch.setData(nodes, edges, sketch.getMetadata());
  if (opts.persist !== false) {
    persistWallSketch(viewer, sketch);
  }
  return edgeId;
}

/**
 * Translucent quad overlay rendered on the scene's THREE.Scene to indicate
 * the hovered wall. One instance per tool; reuses its geometry/material
 * across hover frames and only rewrites the four corner positions when the
 * pick changes. `dispose` releases the GPU buffers when the tool tears down.
 *
 * Styling matches the cyan-on-translucent look the skeleton edge selection
 * uses (`WallSketchSelection`) so the fallback feels visually continuous with
 * the skeleton path.
 */
export class RoomEdgeHoverOverlay {
  private readonly mesh: THREE.Mesh;
  private readonly geometry: THREE.BufferGeometry;
  private readonly material: THREE.MeshBasicMaterial;
  private readonly outlineGeometry: THREE.BufferGeometry;
  private readonly outlineMaterial: THREE.LineBasicMaterial;
  private readonly outline: THREE.LineLoop;
  private readonly scene: THREE.Scene;
  private readonly unregister: () => void;

  constructor(private viewer: Viewer) {
    // Quad as two triangles (0,1,2) + (0,2,3) — same winding as the corner
    // order returned by `buildWallBox`.
    this.geometry = new THREE.BufferGeometry();
    this.geometry.setAttribute('position', new THREE.BufferAttribute(new Float32Array(12), 3));
    this.geometry.setIndex([0, 1, 2, 0, 2, 3]);
    this.material = new THREE.MeshBasicMaterial({
      // Primary brand green (neon-green-500) — the door/window tool's wall
      // hover/selection strips wear the brand color.
      color: 0x7edd64,
      transparent: true,
      opacity: 0.18,
      depthTest: false,
      depthWrite: false,
      side: THREE.DoubleSide,
    });
    this.mesh = new THREE.Mesh(this.geometry, this.material);
    this.mesh.renderOrder = 999;
    this.mesh.frustumCulled = false;
    this.mesh.visible = false;

    this.outlineGeometry = new THREE.BufferGeometry();
    this.outlineGeometry.setAttribute('position', new THREE.BufferAttribute(new Float32Array(12), 3));
    this.outlineMaterial = new THREE.LineBasicMaterial({
      color: 0x7edd64,
      transparent: true,
      opacity: 0.9,
      depthTest: false,
    });
    this.outline = new THREE.LineLoop(this.outlineGeometry, this.outlineMaterial);
    this.outline.renderOrder = 999;
    this.outline.frustumCulled = false;
    this.outline.visible = false;

    // Render in a dedicated TOOL OVERLAY scene, not `viewer.scene.scene`. The
    // EDL pipeline composites the point cloud OVER `scene.scene`, so an overlay
    // there is buried behind the cloud even with depthTest:false. Tool overlay
    // scenes render AFTER the EDL composite pass, so the highlight lands on top
    // of the cloud in both the EDL (3D) and plain (X-Ray) pipelines.
    this.scene = new THREE.Scene();
    this.scene.add(this.mesh);
    this.scene.add(this.outline);
    this.unregister = this.viewer.registerToolOverlayScene(this.scene);
  }

  /** Set the overlay to render `pick`. */
  show(pick: RoomEdgePick): void {
    this.showBox(pick.wallBox);
  }

  /** Render an arbitrary 4-corner strip (e.g. a hovered wall-sketch chain's
   * band) with the same fill + outline visual as the room-edge pick. */
  showBox(corners: readonly THREE.Vector3[]): void {
    const positions = this.geometry.getAttribute('position') as THREE.BufferAttribute;
    const outlinePositions = this.outlineGeometry.getAttribute('position') as THREE.BufferAttribute;
    for (let i = 0; i < 4; i++) {
      const c = corners[i];
      positions.setXYZ(i, c.x, c.y, c.z);
      outlinePositions.setXYZ(i, c.x, c.y, c.z);
    }
    positions.needsUpdate = true;
    outlinePositions.needsUpdate = true;
    this.mesh.visible = true;
    this.outline.visible = true;
  }

  /** Hide the overlay (keeps it mounted; cheap to re-show on the next hover). */
  hide(): void {
    this.mesh.visible = false;
    this.outline.visible = false;
  }

  /** Tear down GPU buffers and detach from the scene. */
  dispose(): void {
    this.hide();
    this.unregister();
    this.scene.remove(this.mesh);
    this.scene.remove(this.outline);
    this.geometry.dispose();
    this.material.dispose();
    this.outlineGeometry.dispose();
    this.outlineMaterial.dispose();
  }
}
